/* ============================================
   ACCOUNT COMPONENT - Profile Management
   Change Avatar / Username / Password
   ============================================ */

import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

export default function Account() {
  const { user, updateProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    displayName: user?.displayName || '',
    username: user?.username || '',
    email: user?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('Image must be less than 2MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      updateProfile({ avatar: reader.result });
      setMessage('Avatar updated successfully');
      setError('');
    };
    reader.readAsDataURL(file);
  };

  const handleUpdateProfile = (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    if (!form.displayName.trim() || !form.username.trim()) {
      setError('Display name and username are required');
      return;
    }
    updateProfile({
      displayName: form.displayName.trim(),
      username: form.username.trim(),
      email: form.email.trim(),
    });
    setMessage('Profile updated successfully');
  };

  const handleChangePassword = (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    if (!form.currentPassword || !form.newPassword) {
      setError('All password fields are required');
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    if (form.newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setMessage('Password changed successfully (demo mode)');
    setForm((prev) => ({ ...prev, currentPassword: '', newPassword: '', confirmPassword: '' }));
  };

  return (
    <div className="account-page">
      <header className="page-header">
        <h1>Account Settings</h1>
        <p>Manage your profile, credentials, and preferences</p>
      </header>

      {message && <div className="alert success">{message}</div>}
      {error && <div className="alert error">{error}</div>}

      <div className="account-layout">
        <aside className="account-tabs">
          <button
            className={activeTab === 'profile' ? 'tab-btn active' : 'tab-btn'}
            onClick={() => setActiveTab('profile')}
          >
            Profile
          </button>
          <button
            className={activeTab === 'password' ? 'tab-btn active' : 'tab-btn'}
            onClick={() => setActiveTab('password')}
          >
            Password
          </button>
          <button
            className={activeTab === 'admin' ? 'tab-btn active' : 'tab-btn'}
            onClick={() => setActiveTab('admin')}
          >
            Admin Users
          </button>
        </aside>

        <div className="account-content">
          {activeTab === 'profile' && (
            <div className="tab-panel">
              <div className="avatar-section">
                <div className="avatar-preview">
                  {user?.avatar ? (
                    <img src={user.avatar} alt="avatar" />
                  ) : (
                    <span>{(user?.displayName || user?.username || 'A').charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <input
                  type="file"
                  accept="image/*"
                  ref={fileRef}
                  style={{ display: 'none' }}
                  onChange={handleAvatarChange}
                />
                <button className="btn-secondary" onClick={() => fileRef.current?.click()}>
                  Change Avatar
                </button>
              </div>

              <form onSubmit={handleUpdateProfile} className="account-form">
                <div className="form-row">
                  <div className="form-field">
                    <label>Display Name</label>
                    <input
                      type="text"
                      name="displayName"
                      value={form.displayName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label>Username</label>
                    <input
                      type="text"
                      name="username"
                      value={form.username}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                <div className="form-field">
                  <label>Email Address</label>
                  <input type="email" name="email" value={form.email} onChange={handleChange} />
                </div>
                <button type="submit" className="btn-primary">Save Changes</button>
              </form>
            </div>
          )}

          {activeTab === 'password' && (
            <div className="tab-panel">
              <form onSubmit={handleChangePassword} className="account-form">
                <div className="form-field">
                  <label>Current Password</label>
                  <input
                    type="password"
                    name="currentPassword"
                    value={form.currentPassword}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-row">
                  <div className="form-field">
                    <label>New Password</label>
                    <input
                      type="password"
                      name="newPassword"
                      value={form.newPassword}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label>Confirm Password</label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={form.confirmPassword}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                <button type="submit" className="btn-primary">Change Password</button>
              </form>
            </div>
          )}

          {activeTab === 'admin' && (
            <div className="tab-panel">
              <div className="admin-notice">
                <strong>Superadmin Access</strong>
                <p>This section allows managing backend admin accounts via WebSocket-connected servers.</p>
              </div>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Username</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Created</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{user?.id}</td>
                    <td>{user?.username}</td>
                    <td><span className="badge superadmin">{user?.role}</span></td>
                    <td><span className="badge active">Active</span></td>
                    <td>{new Date(user?.createdAt).toLocaleDateString()}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .account-page {
          padding: 30px;
          max-width: 900px;
        }
        .page-header {
          margin-bottom: 25px;
        }
        .page-header h1 {
          font-size: 22px;
          color: #222;
          margin-bottom: 6px;
        }
        .page-header p {
          color: #666;
          font-size: 13px;
        }
        .alert {
          padding: 12px 15px;
          border-radius: 3px;
          font-size: 12px;
          margin-bottom: 20px;
          font-weight: bold;
        }
        .alert.success {
          background: #e6f9ed;
          color: #1a7;
          border: 1px solid #b3e6c8;
        }
        .alert.error {
          background: #ffe0e0;
          color: #c33;
          border: 1px solid #fcc;
        }
        .account-layout {
          display: flex;
          gap: 20px;
        }
        .account-tabs {
          width: 160px;
          flex-shrink: 0;
        }
        .tab-btn {
          width: 100%;
          text-align: left;
          padding: 10px 14px;
          background: #fff;
          border: 1px solid #ddd;
          border-bottom: none;
          font-size: 12px;
          color: #555;
          cursor: pointer;
          transition: all 0.15s;
        }
        .tab-btn:first-child {
          border-radius: 4px 4px 0 0;
        }
        .tab-btn:last-child {
          border-bottom: 1px solid #ddd;
          border-radius: 0 0 4px 4px;
        }
        .tab-btn:hover {
          background: #f9f9f9;
        }
        .tab-btn.active {
          background: #ff6c2c;
          color: #fff;
          border-color: #e05216;
          font-weight: bold;
        }
        .account-content {
          flex: 1;
          background: #fff;
          border: 1px solid #ddd;
          border-radius: 4px;
          padding: 25px;
        }
        .tab-panel {
          animation: fadeIn 0.3s ease;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .avatar-section {
          display: flex;
          align-items: center;
          gap: 20px;
          margin-bottom: 25px;
          padding-bottom: 25px;
          border-bottom: 1px solid #eee;
        }
        .avatar-preview {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: linear-gradient(135deg, #ff6c2c, #e05216);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          font-size: 32px;
          font-weight: bold;
          overflow: hidden;
          border: 3px solid #fff;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
        }
        .avatar-preview img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .account-form {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }
        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 15px;
        }
        .form-field {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }
        .form-field label {
          font-size: 12px;
          color: #444;
          font-weight: bold;
        }
        .form-field input {
          padding: 8px 10px;
          border: 1px solid #ccc;
          border-radius: 2px;
          font-size: 13px;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .form-field input:focus {
          border-color: #ff6c2c;
          box-shadow: 0 0 3px rgba(255, 108, 44, 0.3);
        }
        .btn-primary {
          align-self: flex-start;
          padding: 9px 22px;
          background: linear-gradient(to bottom, #ff7a3d 0%, #e05216 100%);
          border: 1px solid #c33d06;
          border-radius: 3px;
          color: #fff;
          font-size: 12px;
          font-weight: bold;
          cursor: pointer;
        }
        .btn-primary:hover {
          opacity: 0.92;
        }
        .btn-secondary {
          padding: 8px 16px;
          background: #fff;
          border: 1px solid #ccc;
          border-radius: 3px;
          color: #444;
          font-size: 12px;
          cursor: pointer;
        }
        .btn-secondary:hover {
          background: #f5f5f5;
        }
        .admin-notice {
          background: #fff8e6;
          border: 1px solid #f0d78c;
          padding: 12px 15px;
          border-radius: 3px;
          font-size: 12px;
          color: #665500;
          margin-bottom: 20px;
        }
        .admin-notice strong {
          display: block;
          margin-bottom: 4px;
        }
        .admin-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
        }
        .admin-table th,
        .admin-table td {
          padding: 10px 12px;
          text-align: left;
          border-bottom: 1px solid #eee;
        }
        .admin-table th {
          background: #f9f9f9;
          font-weight: bold;
          color: #555;
        }
        .badge {
          display: inline-block;
          padding: 3px 8px;
          border-radius: 10px;
          font-size: 10px;
          font-weight: bold;
          text-transform: uppercase;
        }
        .badge.superadmin {
          background: #ffe0cc;
          color: #c44;
        }
        .badge.active {
          background: #e6f9ed;
          color: #1a7;
        }
        @media (max-width: 768px) {
          .account-layout {
            flex-direction: column;
          }
          .account-tabs {
            width: 100%;
            display: flex;
          }
          .tab-btn {
            border-bottom: 1px solid #ddd;
            border-radius: 0 !important;
          }
          .form-row {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}
